import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Bell, Bot, Building2, RefreshCw, Tags, Database, Lock, Mail, Palette, PenLine, Search, SearchX, Shield, Sparkles, Workflow } from 'lucide-react';
import { spring } from '../../components/ui/motion';
import { Notice } from '../../components/ui/Feedback';
import { Input } from '../../components/ui/Form';
import { useCurrentWorkspace } from '../../hooks/useWorkspace';
import { useBreakpoint } from '../../hooks/useBrowser';

/** Each section has its own vivid tone (the same palette as the page headers), used for its header tile and active nav icon. */
const TONE_TILE = {
  lime: 'mp-pill-lime',
  yellow: 'mp-pill-yellow',
  lavender: 'mp-pill-lavender',
  sky: 'mp-pill-sky',
  mint: 'mp-pill-mint',
} as const;
type SectionTone = keyof typeof TONE_TILE;

export const SETTINGS_SECTIONS = [
  { group: 'Workspace', items: [
    { path: 'organization', label: 'Organization & team', icon: Building2, hint: 'Members, divisions, invites', tone: 'sky' },
  ] },
  { group: 'Mail', items: [
    { path: 'accounts', label: 'Email accounts', icon: Mail, hint: 'Gmail, Outlook, IMAP', tone: 'lime' },
    { path: 'signatures', label: 'Signatures', icon: PenLine, hint: 'Text & HTML, per account', tone: 'mint' },
  ] },
  { group: 'Assistant', items: [
    { path: 'categories', label: 'Categories', icon: Tags, hint: 'How mail is sorted', tone: 'yellow' },
    { path: 'responders', label: 'Responders & rules', icon: Workflow, hint: 'Acknowledge, auto-reply', tone: 'lavender' },
    { path: 'ai', label: 'AI providers & privacy', icon: Bot, hint: 'Keys, consent, redaction', tone: 'sky' },
    { path: 'preferences', label: 'AI preferences', icon: Sparkles, hint: 'Style, tone, examples', tone: 'lavender' },
  ] },
  { group: 'App', items: [
    { path: 'notifications', label: 'Notifications & ringing', icon: Bell, hint: 'Island, sounds, quiet hours', tone: 'yellow' },
    { path: 'appearance', label: 'Appearance', icon: Palette, hint: 'Theme, accent, text size', tone: 'lavender' },
    { path: 'security', label: 'Security & devices', icon: Shield, hint: 'Sessions, password', tone: 'mint' },
    { path: 'data', label: 'Data & audit log', icon: Database, hint: 'Retention, export, delete', tone: 'sky' },
    { path: 'updates', label: 'App & updates', icon: RefreshCw, hint: 'Version & desktop updates', tone: 'lime' },
  ] },
] as const satisfies readonly { group: string; items: readonly { path: string; label: string; icon: unknown; hint: string; tone: SectionTone }[] }[];

type SectionItem = (typeof SETTINGS_SECTIONS)[number]['items'][number];
const ALL_ITEMS: readonly SectionItem[] = SETTINGS_SECTIONS.flatMap((g) => [...g.items]);

/** Sections an organization's owners/admins manage for everyone; members can look but not change. */
const ADMIN_MANAGED: Record<string, string> = {
  responders: 'Responder rules',
  ai: 'AI providers and privacy settings',
  preferences: 'AI preferences',
  signatures: 'Signatures',
  data: 'Data export and the audit log',
};

// ---------------------------------------------------------------------------
// Header slot: pages describe themselves with <SettingsTitle>, the layout renders it
// as the sticky header of the content panel (full width, above the scrolling body).
// ---------------------------------------------------------------------------

interface HeaderState {
  title: string;
  description?: string;
}
const HeaderCtx = createContext<((h: HeaderState | null) => void) | null>(null);

/** Section title used at the top of each settings sub-page. */
export function SettingsTitle({ title, description }: { title: string; description?: string }) {
  const set = useContext(HeaderCtx);
  useLayoutEffect(() => {
    if (!set) return;
    set({ title, description });
    return () => set(null);
  }, [set, title, description]);
  if (set) return null;
  // Rendered outside the settings layout (should not happen) — fall back to an inline header.
  return (
    <header className="mb-5">
      <h2 className="text-xl font-semibold text-ink">{title}</h2>
      {description && <p className="mt-1 max-w-2xl text-[0.8125rem] text-muted">{description}</p>}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function matches(it: SectionItem, group: string, q: string) {
  if (!q) return true;
  const hay = `${it.label} ${it.hint} ${group}`.toLowerCase();
  return q.split(/\s+/).every((w) => hay.includes(w));
}

export function SettingsLayout() {
  const location = useLocation();
  const bp = useBreakpoint();
  const { workspace, isOrg, isAdmin } = useCurrentWorkspace();
  const section = location.pathname.split('/')[2] ?? '';
  const managed = isOrg && !isAdmin ? ADMIN_MANAGED[section] : undefined;
  const current = ALL_ITEMS.find((it) => it.path === section);
  const [header, setHeader] = useState<HeaderState | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Each section starts at the top of its own content panel.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [section]);

  const Icon = current?.icon ?? Shield;
  const title = header?.title ?? current?.label ?? 'Settings';
  const description = header?.description;

  return (
    <HeaderCtx.Provider value={setHeader}>
      <div className="flex h-full min-h-0 flex-col gap-3 p-3 md:flex-row md:p-4">
        {bp === 'mobile' ? <MobileIndex section={section} /> : <IndexPanel section={section} />}

        <section aria-labelledby="settings-section-title" className="mp-panel flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <header className="flex items-start gap-3.5 border-b border-hairline px-5 py-4 md:px-6 md:py-5">
            <div aria-hidden className={clsx('mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px]', current ? TONE_TILE[current.tone] : 'mp-pill-sky')}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 id="settings-section-title" className="text-xl font-semibold leading-tight text-ink">
                {title}
              </h1>
              {description && <p className="mt-1 line-clamp-2 max-w-3xl text-[0.8125rem] leading-relaxed text-muted md:line-clamp-none">{description}</p>}
            </div>
          </header>
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto scroll-thin">
            <div className="mx-auto w-full max-w-[60rem] px-4 pb-24 pt-5 md:px-6 md:pb-10 md:pt-6">
              {managed && (
                <Notice tone="info" icon={<Lock className="h-4 w-4" />} className="mb-6">
                  {managed} are managed by the owners and admins of {workspace?.name}. You can view them, but changes need an admin.
                </Notice>
              )}
              <Outlet />
            </div>
          </div>
        </section>
      </div>
    </HeaderCtx.Provider>
  );
}

function IndexPanel({ section }: { section: string }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const groups = useMemo(() => SETTINGS_SECTIONS.map((g) => ({ group: g.group, items: g.items.filter((it) => matches(it, g.group, q)) })).filter((g) => g.items.length > 0), [q]);

  return (
    <nav aria-label="Settings" className="mp-panel flex w-full shrink-0 flex-col overflow-hidden md:w-[260px]">
      <div className="space-y-2.5 px-4 pb-2.5 pt-3.5">
        <p className="px-1 text-[0.9375rem] font-semibold text-ink">Settings</p>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" aria-hidden />
          <Input
            type="search"
            aria-label="Search settings"
            placeholder="Search settings"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && query) {
                e.stopPropagation();
                setQuery('');
              }
            }}
            className="!h-9 !rounded-xl !bg-raised pl-9 text-[0.8125rem] focus:!bg-surface"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {groups.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-3 py-8 text-center">
            <SearchX className="h-5 w-5 text-subtle" aria-hidden />
            <p className="text-[0.8125rem] text-muted">No settings match “{query.trim()}”.</p>
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.group} className="mb-1.5 last:mb-0">
              <p className="px-3 pb-1 pt-2.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">{g.group}</p>
              <ul className="space-y-0.5">
                {g.items.map((it) => {
                  const active = section === it.path;
                  const ItemIcon = it.icon;
                  return (
                    <li key={it.path}>
                      <NavLink
                        to={`/settings/${it.path}`}
                        aria-current={active ? 'page' : undefined}
                        className={clsx('group relative flex items-center gap-3 rounded-xl py-[7px] pl-3 pr-2.5 transition-colors', active ? 'text-primary-ink' : 'text-muted hover:bg-raised hover:text-ink')}
                      >
                        {active && <motion.span layoutId="settings-active" transition={spring} className="absolute inset-0 rounded-xl bg-primary-soft" aria-hidden />}
                        {active && <motion.span layoutId="settings-active-bar" transition={spring} className="absolute bottom-2 left-0 top-2 w-[3px] rounded-full bg-primary" aria-hidden />}
                        <span
                          className={clsx(
                            'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] transition-[background-color,color,transform]',
                            active ? clsx(TONE_TILE[it.tone], 'shadow-sm') : 'bg-sunken/70 text-muted group-hover:scale-105 group-hover:bg-surface group-hover:text-ink',
                          )}
                        >
                          <ItemIcon className="h-[18px] w-[18px]" aria-hidden />
                        </span>
                        <span className="relative min-w-0">
                          <span className={clsx('block truncate text-[0.8125rem] font-medium leading-tight', active ? 'text-primary-ink' : 'text-ink')}>{it.label}</span>
                          <span className={clsx('mt-0.5 block truncate text-[0.6875rem] leading-tight', active ? 'text-primary-ink/70' : 'text-muted')}>{it.hint}</span>
                        </span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>
    </nav>
  );
}

function MobileIndex({ section }: { section: string }) {
  const listRef = useRef<HTMLDivElement>(null);
  // Keep the active chip in view when the section changes.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [section]);
  return (
    <nav aria-label="Settings" className="mp-panel shrink-0 overflow-hidden">
      <div ref={listRef} className="flex gap-1 overflow-x-auto px-2 py-2 scroll-thin [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {ALL_ITEMS.map((it) => {
          const active = section === it.path;
          const ItemIcon = it.icon;
          return (
            <NavLink key={it.path} to={`/settings/${it.path}`} aria-current={active ? 'page' : undefined} className={clsx('mp-chip shrink-0', active && 'is-active')}>
              <ItemIcon className="h-4 w-4" aria-hidden />
              {it.label}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
